"use client"

import * as React from "react"
import { useState, useEffect } from "react"
import axios from "axios"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { X, Upload, Image as ImageIcon, Loader2 } from "lucide-react"
import { BASE_URL } from "@/config";
import { toast } from "sonner";

interface VehicleImagesProps {
  isOpen: boolean
  onClose: () => void
  vehicle: any
}

interface VehicleImage {
  id: number
  image: string
  image_uid: string
  name: string
  size?: number
}

export const VehicleImages: React.FC<VehicleImagesProps> = ({
  isOpen,
  onClose,
  vehicle
}) => {
  const [images, setImages] = useState<VehicleImage[]>([])
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [loading, setLoading] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [confirmingDelete, setConfirmingDelete] = useState(false)
  const [imageToDelete, setImageToDelete] = useState<{ id: number; name: string } | null>(null)

  // Get auth token from localStorage
  const getAuthToken = () => {
    return localStorage.getItem("access_token")
  }

  // Fetch images from API
  const fetchImages = async () => {
    if (!vehicle?.id) return

    setLoading(true)
    try {
      const token = getAuthToken()
      const response = await axios.get(`${BASE_URL}/upload/vehicle/${vehicle.id}/image`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
      setImages(response.data || [])
    } catch (error) {
      console.error('Failed to fetch images:', error)
      setImages([])
    } finally {
      setLoading(false)
    }
  }

  // Load images when vehicle changes or sheet opens
  useEffect(() => {
    if (isOpen && vehicle?.id) {
      fetchImages()
    }
  }, [isOpen, vehicle?.id])

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      setSelectedFile(file)
    }
  }

  const handleUpload = async () => {
    if (!selectedFile || !vehicle?.id) return

    setUploading(true)
    try {
      const token = getAuthToken()
      const formData = new FormData()
      formData.append('file', selectedFile)

      await axios.post(`${BASE_URL}/upload/vehicle/${vehicle.id}/image`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      })

      // Refresh images after successful upload
      await fetchImages()
      setSelectedFile(null)
      
      // Reset the file input
      const fileInput = document.getElementById('file-upload') as HTMLInputElement
      if (fileInput) {
        fileInput.value = ''
      }
    } catch (error) {
      console.error('Failed to upload image:', error)
    } finally {
      setUploading(false)
    }
  }

  const confirmDeleteImage = (image: { id: number; name: string }) => {
    setImageToDelete(image)
    setConfirmingDelete(true)
  }

  const handleConfirmDelete = async () => {
    if (!imageToDelete) return
    
    try {
      const token = getAuthToken()
      await axios.delete(`${BASE_URL}/upload/vehicle/${vehicle.id}/image/${imageToDelete.id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      // Refresh images after successful deletion
      await fetchImages()
      toast.success("Image deleted successfully")
    } catch (error) {
      console.error('Failed to delete image:', error)
      toast.error("Failed to delete image")
    } finally {
      setConfirmingDelete(false)
      setImageToDelete(null)
    }
  }

  const handleCancelDelete = () => {
    setConfirmingDelete(false)
    setImageToDelete(null)
  }

  const vehicleName = vehicle ? 
    `${vehicle.make?.name || ''} ${vehicle.make_model || ''} (${vehicle.license_plate || 'N/A'})`.trim() : 
    'Unknown Vehicle'

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent side="bottom" className="h-[80vh] overflow-y-auto p-4">
        <SheetHeader>
          <SheetTitle>Vehicle Images</SheetTitle>
          <SheetDescription>
            Manage images for {vehicleName}
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-6">

          {/* Upload and Images Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* File Upload Section */}
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Upload className="w-5 h-5 text-primary" />
                <Label htmlFor="file-upload" className="text-base font-medium">
                  Upload New Image
                </Label>
              </div>
              
              <div className="space-y-3">
                <Input
                  id="file-upload"
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                />
                
                {selectedFile && (
                  <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                    <div className="flex items-center gap-2">
                      <ImageIcon className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm font-medium">{selectedFile.name}</span>
                      <span className="text-xs text-muted-foreground">
                        ({(selectedFile.size / 1024).toFixed(1)} KB)
                      </span>
                    </div>
                    <Button 
                      onClick={handleUpload}
                      size="sm"
                      disabled={uploading}
                    >
                      {uploading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Uploading...
                        </>
                      ) : (
                        'Upload'
                      )}
                    </Button>
                  </div>
                )}
                
                {!selectedFile && (
                  <div className="flex items-center justify-center p-8 border-2 border-dashed border-border rounded-lg bg-muted/30">
                    <div className="text-center">
                      <ImageIcon className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                      <p className="text-sm text-muted-foreground">
                        Choose an image file to upload
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Supports JPG, PNG, GIF up to 10MB
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Images Grid */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <ImageIcon className="w-5 h-5 text-primary" />
                  <Label className="text-base font-medium">
                    Vehicle Images
                  </Label>
                </div>
                <div className="text-sm text-muted-foreground">
                  {images.length} {images.length === 1 ? 'image' : 'images'}
                </div>
              </div>
              
              {images.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8">
                  {loading ? (
                    <>
                      <Loader2 className="w-12 h-12 text-muted-foreground mb-3 animate-spin" />
                      <p className="text-sm text-muted-foreground">Loading images...</p>
                    </>
                  ) : (
                    <>
                      <ImageIcon className="w-12 h-12 text-muted-foreground mb-3" />
                      <h4 className="text-base font-medium text-muted-foreground mb-2">No images uploaded</h4>
                      <p className="text-sm text-muted-foreground text-center">
                        Upload images to showcase this vehicle.
                      </p>
                    </>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 max-h-96 overflow-y-auto">
                  {images.map((image) => (
                    <div key={image.id} className="space-y-2">
                      <div className="relative group">
                        <div className="relative aspect-square overflow-hidden rounded-lg border bg-background">
                          <img
                            src={image.image}
                            alt={image.name}
                            className="w-full h-full object-cover transition-all duration-200 group-hover:scale-110"
                          />
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-200" />
                          <Button
                            variant="destructive"
                            size="icon"
                            className="absolute top-1 right-1 w-6 h-6 opacity-0 group-hover:opacity-100 transition-opacity duration-200 shadow-lg"
                            onClick={(e) => {
                              e.preventDefault()
                              e.stopPropagation()
                              confirmDeleteImage({ id: image.id, name: image.name })
                            }}
                          >
                            <X className="w-3 h-3" />
                          </Button>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1 truncate">
                          {image.name}
                        </p>
                      </div>
                      
                      {confirmingDelete && imageToDelete?.id === image.id && (
                        <div className="bg-destructive/10 border border-destructive/20 rounded-md p-3 space-y-3">
                          <div className="flex items-center gap-2 text-destructive">
                            <X className="w-4 h-4" />
                            <span className="font-medium text-sm">Confirm Deletion</span>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            Are you sure you want to delete this image? This action cannot be undone.
                          </p>
                          <div className="flex items-center gap-2">
                            <Button 
                              variant="destructive" 
                              size="sm"
                              onClick={handleConfirmDelete}
                            >
                              Delete
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={handleCancelDelete}
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  )
}

export default VehicleImages